import React, { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { brands } from "../config/config";
import { apiUrl } from "../config/config";

const InstantQuoteWidget = () => {
  const [open, setOpen] = useState(false);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    patch_type: "",
    backing_type: "",
    quantity: "",
    width: "",
    height: "",
    comments: "",
  });

  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({});

  const modalRef = useRef(null);
  const location = useLocation();

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm({
      ...form,
      [name]: value,
    });

    setErrors({
      ...errors,
      [name]: "",
    });
  };

  useEffect(() => {
    setOpen(false);
  }, [location]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    if (open) document.addEventListener("mousedown", handleClickOutside);

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const validate = () => {
    let newErrors = {};

    if (!form.name.trim()) newErrors.name = "Name required";

    if (!form.email) {
      newErrors.email = "Email required";
    } else if (!/\S+@\S+\.\S+/.test(form.email)) {
      newErrors.email = "Invalid email";
    }

    if (!form.phone) newErrors.phone = "Phone required";
    if (!form.quantity) newErrors.quantity = "Quantity required";
    if (!form.width) newErrors.width = "Width required";
    if (!form.height) newErrors.height = "Height required";

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const submitForm = async () => {
    if (!validate()) return;

    const formData = new FormData();

    formData.append("name", form.name);
    formData.append("email", form.email);
    formData.append("phone", form.phone);
    formData.append("patch_type", form.patch_type);
    formData.append("backing_type", form.backing_type);
    formData.append("quantity", parseInt(form.quantity));
    formData.append("width", parseFloat(form.width));
    formData.append("height", parseFloat(form.height));
    formData.append("comments", form.comments);
    formData.append("brand", brands);

    if (file) formData.append("image", file);

    try {
      const response = await fetch(`${apiUrl}/api/instant-quote`, {
        method: "POST",
        headers: {
          Accept: "application/json",
        },
        body: formData,
      });

      const data = await response.json();

      if (data.errors) {
        setErrors(data.errors);
        return;
      }

      if (data.status) {
        alert("Quote Submitted!");

        setForm({
          name: "",
          email: "",
          phone: "",
          patch_type: "",
          backing_type: "",
          quantity: "",
          width: "",
          height: "",
          comments: "",
        });

        setFile(null);
        setOpen(false);
      }
    } catch (err) {
      alert("Server Error");
    }
  };

  return (
    <>
      {/* Floating Button */}
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="fixed bottom-6 left-6 z-[9999] flex h-16 w-16 items-center justify-center rounded-2xl border border-pastelPurple bg-keyLime text-2xl text-darkPurple shadow-[0_16px_35px_rgba(47,34,84,0.22)] transition-all duration-300 hover:-translate-y-1 hover:bg-[#c8f531]"
        aria-label="Open instant quote form"
      >
        💬
      </button>

      {open && (
        <div
          ref={modalRef}
          className="fixed bottom-24 left-4 z-[9999] w-[calc(100vw-32px)] overflow-hidden rounded-[28px] border border-pastelPurple bg-white shadow-[0_24px_70px_rgba(47,34,84,0.24)] sm:left-6 sm:w-[420px]"
        >
          {/* Header */}
          <div className="relative overflow-hidden bg-darkPurple px-5 py-4 text-white">
            <div className="absolute -left-20 -top-20 h-56 w-56 rounded-full bg-pastelPurple/30 blur-3xl"></div>
            <div className="absolute -right-20 -bottom-20 h-56 w-56 rounded-full bg-keyLime/25 blur-3xl"></div>

            <div className="relative flex w-full items-center justify-between gap-4">
              <div className="min-w-0 flex-1 pr-2">
                <h3 className="whitespace-normal break-words text-lg font-extrabold leading-tight text-white">
                  Get Instant Quote
                </h3>
                <p className="mt-1 text-xs font-medium leading-5 text-[#f2ecff]">
                  Fill details and submit your quote
                </p>
              </div>

              <button
                type="button"
                onClick={() => setOpen(false)}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/10 text-base font-extrabold text-white transition hover:bg-keyLime hover:text-darkPurple"
                aria-label="Close instant quote form"
              >
                ✕
              </button>
            </div>
          </div>

          {/* Form */}
          <div className="max-h-[min(70vh,520px)] space-y-3 overflow-y-auto bg-[#fbf9ff] p-4">
            <InputField
              name="name"
              placeholder="Name"
              value={form.name}
              onChange={handleChange}
              error={errors.name}
            />

            <InputField
              name="email"
              placeholder="Email"
              value={form.email}
              onChange={handleChange}
              error={errors.email}
            />

            <InputField
              name="phone"
              type="number"
              placeholder="Phone"
              value={form.phone}
              onChange={handleChange}
              error={errors.phone}
            />

            <select
              name="patch_type"
              value={form.patch_type}
              onChange={handleChange}
              className={inputStyle}
            >
              <option value="">Select Patch Type</option>
              <option value="Standard Embroidery">Standard Embroidery</option>
              <option value="3D Embroidery">3D Embroidery</option>
              <option value="PVC Rubber">PVC Rubber</option>
              <option value="Leather">Leather</option>
              <option value="Chenille">Chenille</option>
              <option value="Woven">Woven</option>
              <option value="Printed">Printed</option>
              <option value="Bullion">Bullion</option>
              <option value="Key Chain">Key Chain</option>
            </select>

            <select
              name="backing_type"
              value={form.backing_type}
              onChange={handleChange}
              className={inputStyle}
            >
              <option value="">Select Backing Type</option>
              <option value="Iron-On">Iron-On</option>
              <option value="Velcro">Velcro</option>
              <option value="Plastic">Plastic</option>
              <option value="Tape/Adhesive">Tape/Adhesive</option>
              <option value="Sew-On">Sew-On</option>
            </select>

            <InputField
              type="number"
              name="quantity"
              placeholder="Quantity"
              value={form.quantity}
              onChange={handleChange}
              error={errors.quantity}
            />

            <div className="grid grid-cols-2 gap-2">
              <InputField
                type="number"
                name="width"
                placeholder="Width"
                value={form.width}
                onChange={handleChange}
                error={errors.width}
              />

              <InputField
                type="number"
                name="height"
                placeholder="Height"
                value={form.height}
                onChange={handleChange}
                error={errors.height}
              />
            </div>

            <input
              type="file"
              onChange={(e) => setFile(e.target.files[0])}
              className={inputStyle}
            />

            <textarea
              name="comments"
              placeholder="Comments"
              value={form.comments}
              onChange={handleChange}
              className={`${inputStyle} h-24 resize-none`}
            />

            <button
              type="button"
              onClick={submitForm}
              className="w-full rounded-2xl bg-keyLime px-6 py-3 text-sm font-extrabold text-darkPurple shadow-[0_12px_26px_rgba(215,255,79,0.35)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#c8f531]"
            >
              Submit Quote
            </button>
          </div>
        </div>
      )}
    </>
  );
};

const InputField = ({
  type = "text",
  name,
  placeholder,
  value,
  onChange,
  error,
}) => (
  <div className="w-full">
    <input
      type={type}
      name={name}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      className={`${inputStyle} ${error ? "border-red-500" : ""}`}
    />

    {error && <p className="mt-1 text-xs font-bold text-red-500">{error}</p>}
  </div>
);

const inputStyle =
  "w-full rounded-2xl border border-pastelPurple bg-white px-4 py-3 text-sm font-medium text-darkPurple outline-none transition placeholder:text-[#8a829a] focus:border-themePurple focus:bg-white focus:ring-4 focus:ring-pastelPurple/60";

export default InstantQuoteWidget;